import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';

export const PRIVATE_WORKSPACE_AGENT_TABLE_ARTIFACT_ROWS_KEY = 'PRIVATE_WORKSPACE_AGENT_TABLE_ARTIFACT_ROWS';

/**
 * Rows are two short strings, so a page is cheap to send. 100 keeps the first paint fast, and the
 * card asks for the next page as soon as the user reaches the last loaded one, so it is invisible
 * in practice. The endpoint accepts up to 500.
 */
export const WORKSPACE_AGENT_TABLE_ARTIFACT_ROW_PAGE_LIMIT = 100;
const TABLE_ARTIFACT_ROW_TRANSIENT_RETRIES = 1;

export type PrivateWorkspaceAgentTableArtifactRowsParams = {
  artifactId: string | undefined;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentTableArtifactRowsKey = [
  typeof PRIVATE_WORKSPACE_AGENT_TABLE_ARTIFACT_ROWS_KEY,
  PrivateWorkspaceAgentTableArtifactRowsParams,
];

export const getPrivateWorkspaceAgentTableArtifactRowsKey = (
  params: PrivateWorkspaceAgentTableArtifactRowsParams,
): PrivateWorkspaceAgentTableArtifactRowsKey => [PRIVATE_WORKSPACE_AGENT_TABLE_ARTIFACT_ROWS_KEY, params];

export const isPrivateWorkspaceAgentTableArtifactRowsKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentTableArtifactRowsKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_TABLE_ARTIFACT_ROWS_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentTableArtifactRowsOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentTableArtifactRows = (
  params: PrivateWorkspaceAgentTableArtifactRowsParams,
  options?: UsePrivateWorkspaceAgentTableArtifactRowsOptions,
) => {
  const { artifactId, sessionId, workspaceId } = params;
  const canFetch = Boolean(workspaceId) && Boolean(sessionId) && Boolean(artifactId);

  return useInfiniteQuery({
    enabled: canFetch && (options?.enabled ?? true),
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    initialPageParam: '',
    queryFn:
      workspaceId && sessionId && artifactId
        ? ({ pageParam }: { pageParam: string }) =>
            callAirApi(() =>
              Agents.Sessions.Artifacts.Rows.list({
                agentType: 'workspace',
                artifactId,
                cursor: pageParam || undefined,
                limit: WORKSPACE_AGENT_TABLE_ARTIFACT_ROW_PAGE_LIMIT,
                sessionId,
                workspaceId,
              }),
            )
        : skipToken,
    queryKey: getPrivateWorkspaceAgentTableArtifactRowsKey(params),
    retry: (failureCount, error) =>
      !isWorkspaceAgentSessionUnresolvableError(error) && failureCount < TABLE_ARTIFACT_ROW_TRANSIENT_RETRIES,
  });
};
